export type ApiEnvelope<T> = {
  data: T;
  meta: { requestId: string };
  page?: { nextCursor: string | null; hasMore: boolean; limit: number };
};

export type ApiFailure = {
  error: {
    code: string;
    message: string;
    requestId?: string;
    fields?: Record<string, string[]>;
  };
};

export class ApiClientError extends Error {
  public constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly requestId?: string,
    public readonly fields?: Record<string, string[]>
  ) {
    super(message);
    this.name = "ApiClientError";
  }
}

type ApiOptions = Omit<RequestInit, "body"> & {
  body?: unknown;
  idempotencyKey?: string;
};

export async function api<T>(path: string, options: ApiOptions = {}): Promise<ApiEnvelope<T>> {
  const headers = new Headers(options.headers);
  const method = options.method?.toUpperCase() ?? "GET";
  let requestBody: BodyInit | undefined;

  if (options.body instanceof FormData) {
    requestBody = options.body;
  } else if (options.body !== undefined) {
    headers.set("Content-Type", "application/json");
    requestBody = JSON.stringify(options.body);
  }

  if (!new Set(["GET", "HEAD", "OPTIONS"]).has(method)) {
    const csrf = readCookie("tamween_csrf");
    if (csrf) headers.set("X-CSRF-Token", csrf);
  }
  if (options.idempotencyKey) headers.set("Idempotency-Key", options.idempotencyKey);

  const response = await fetch(`/api/v1${path}`, {
    ...options,
    body: requestBody,
    credentials: "same-origin",
    headers
  });
  const contentType = response.headers.get("Content-Type") ?? "";
  const payload = contentType.includes("application/json")
    ? ((await response.json()) as ApiEnvelope<T> | ApiFailure)
    : null;

  if (!response.ok) {
    const failure = payload as ApiFailure | null;
    throw new ApiClientError(
      response.status,
      failure?.error.code ?? "REQUEST_FAILED",
      failure?.error.message ?? "تعذر إكمال الطلب. حاول مرة أخرى.",
      failure?.error.requestId,
      failure?.error.fields
    );
  }
  if (!payload || !("data" in payload))
    throw new ApiClientError(502, "INVALID_RESPONSE", "وصلت استجابة غير مكتملة.");
  return payload;
}

export function readCookie(name: string): string | null {
  const prefix = `${encodeURIComponent(name)}=`;
  const item = document.cookie
    .split(";")
    .map((value) => value.trim())
    .find((value) => value.startsWith(prefix));
  return item ? decodeURIComponent(item.slice(prefix.length)) : null;
}

export function formatApiError(error: unknown): string {
  return error instanceof ApiClientError ? error.message : "حدث خطأ غير متوقع. حاول مرة أخرى.";
}

export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}
